import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Box, Checkbox, FormControlLabel, FormGroup, FormLabel, Stack } from '@mui/material';
import * as state from '@/components/AssetLabelling/state';
import { notEmpty } from '@/utils';
import { CuboidClass } from '@/types';

export const CuboidClassesVisibility = () => {
  const structure = useAtomValue(state.structure);
  const [cuboidClassById, setCuboidClassById] = useAtom(state.cuboidClassById);

  const cuboidClasses = useMemo(() => structure?.cuboidClasses?.filter(notEmpty), [structure]);

  const isVisible = useCallback(
    (cuboidClassId: string) => {
      if (!cuboidClassById) return true;
      return cuboidClassById[cuboidClassId].isVisible ?? true;
    },
    [cuboidClassById]
  );

  const toggleIsVisible = useCallback(
    (cuboidClassId: string) => {
      setCuboidClassById((current) => {
        if (!current) return undefined;
        const entry = current[cuboidClassId];
        const updatedEntry = { ...entry, isVisible: !entry.isVisible };
        return { ...current, [cuboidClassId]: updatedEntry };
      });
    },
    [setCuboidClassById]
  );

  const isAllVisible = useCallback(() => {
    const hasInvisibleClasses = cuboidClasses?.some(
      ({ id }) => !(cuboidClassById?.[id].isVisible ?? true)
    );
    return !hasInvisibleClasses;
  }, [cuboidClasses, cuboidClassById]);

  const isAllInvisible = useCallback(() => {
    const hasVisibleClasses = cuboidClasses?.some(
      ({ id }) => cuboidClassById?.[id].isVisible ?? true
    );
    return !hasVisibleClasses;
  }, [cuboidClasses, cuboidClassById]);

  const toggleIsAllVisible = useCallback(() => {
    if (!cuboidClassById) return;
    if (!cuboidClasses) return;
    const newIsVisible = !isAllVisible();
    setCuboidClassById((current) => {
      if (!current) return current;
      const newCuboidClassById: { [id: string]: CuboidClass } = {};
      cuboidClasses.forEach(({ id }) => {
        newCuboidClassById[id] = { ...current[id], isVisible: newIsVisible };
      });
      return newCuboidClassById;
    });
  }, [cuboidClasses, cuboidClassById, isAllVisible, setCuboidClassById]);

  return (
    <Stack direction="column">
      <FormGroup>
        <FormLabel sx={{ padding: '15px 0px 5px' }}>Cuboid Classes</FormLabel>
        <Stack direction="row" alignItems="center" key="all-cuboid-classes-checkbox">
          <Box
            style={{
              width: '20px',
              height: '20px',
              margin: '5px 10px',
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isAllVisible()}
                indeterminate={!isAllVisible() && !isAllInvisible()}
                onChange={() => toggleIsAllVisible()}
              />
            }
            label="All classes"
          />
        </Stack>
        {structure?.cuboidClasses?.filter(notEmpty).map(({ id, name, color }) => (
          <Stack direction="row" alignItems="center" key={id}>
            <Box
              style={{
                backgroundColor: color,
                width: '20px',
                height: '20px',
                border: '1px solid #555',
                margin: '5px 10px',
              }}
            />

            <FormControlLabel
              control={<Checkbox checked={isVisible(id)} onChange={() => toggleIsVisible(id)} />}
              label={name}
            />
          </Stack>
        ))}
      </FormGroup>
    </Stack>
  );
};
